import { useContext } from 'react'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import EntitySelector from '@components/EntitySelector'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import TeamMemberListItem from './components/TeamMemberListItem'
import useCreateOrEditAgencyTeamMemberModal from './hooks/useCreateOrEditAgencyTeamMemberModal'

const MODAL_KEY = 'CreateOrEditAgencyTeamMemberModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTeamMember: (payload: HandleCallbackActionParams) => void,
  updateTeamMember: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditAgencyTeamMemberModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createTeamMember, updateTeamMember } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const teamMember = digObject(modalPayload, 'teamMember', {})

  const newRecord = !teamMember.id

  const {
    allowedTeamMemberRoles,
    callbacks: {
      createTeamMember: createFn,
      updateTeamMember: updateFn,
    },
    creating,
    filteredTeamMembers,
    hasFilteredTeamMembers,
    teamMemberFormPayload,
    updating,
  } = useCreateOrEditAgencyTeamMemberModal(teamMember)

  const {
    entityState,
    entityState: {
      member_role,
      user_id,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = teamMemberFormPayload

  const createOrUpdateTeamMemberPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: newRecord ? () => setEntityState({ user_id: undefined }) : closeModal,
    },
    entityParams: entityState,
    toastText: `Team Member ${newRecord ? 'Added' : 'Updated'}`,
  }

  const updateButtonText = saveEnabled ? 'Update Team Member' : 'Complete Fields'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Team Member" />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            callbacks={{
              // @ts-expect-error TS2322
              selectItem: (user: { id: number }) => {
                // @ts-expect-error TS2339
                const memberRole = user?.actable_type === 'Agent' ? 'associate_agent' : 'office_support'
                setEntityState({ user_id: user?.id, member_role: memberRole })
              },
            }}
            disabled={!newRecord}
            entityKey="users"
            errorMessage={errors.user_id}
            label="* User"
            requestOptions={{
              // @ts-expect-error TS2783
              include_image_sizes: true,
              // @ts-expect-error TS2783
              include_user_image: true,
              ...defaultRequestOptions.teamMember,
            }}
            selectedIds={filteredTeamMembers.map(member => member.user_id)}
            selectedItemId={user_id}
          />

          <Form.Field
            label="Role"
            marginTop="large"
          >
            {/*
             // @ts-expect-error TS2322 */}
            <select
              disabled={!user_id}
              name="member_role"
              value={member_role}
              {...handlers}
            >
              <option value="">Please Select...</option>
              {allowedTeamMemberRoles.map(role => (
                <option key={role.key} value={role.key}>
                  {role.title}
                </option>
              ))}
            </select>
          </Form.Field>

          {member_role === 'lead_agent' && (
            <SidebarNoticeBox boxProps={{ marginTop: 'large', marginBottom: 'none' }}>
              <SidebarNoticeBox.Title>
                A team can only have one Lead Agent.
              </SidebarNoticeBox.Title>

              <SidebarNoticeBox.Paragraph>
                You are setting this team member to Lead Agent and changing the existing Lead to Associate Agent.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          )}
        </Form>

        {newRecord && (
          <Button
            buttonStyle="secondaryEdit"
            disabled={!saveEnabled}
            loading={creating || updating}
            loadingBubbleColor="white"
            marginTop="large"
            onClick={() => createTeamMember(createOrUpdateTeamMemberPayload)}
            size="medium"
          >
            {saveEnabled ? 'Create Team Member' : 'Complete Fields'}
          </Button>
        )}

        {newRecord && hasFilteredTeamMembers && (
          <>
            <SectionDivider marginTop="large">TEAM MEMBERS</SectionDivider>

            {Object.values(filteredTeamMembers).map(member => (
              <TeamMemberListItem teamMember={member} />
            ))}
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle={newRecord ? 'secondaryUtility' : 'primaryCreate'}
          disabled={newRecord ? false : !saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => closeModal()
              : () => updateTeamMember(createOrUpdateTeamMemberPayload)
          }
          size="large"
        >
          {newRecord ? 'Close' : updateButtonText }
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAgencyTeamMemberModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
